import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui';
import { Wallet, AlertCircle } from 'lucide-react';
import {
  walletService,
  PendingDeposit,
  ActivationFee,
  DepositPaymentResponse,
} from '../../services/wallet.service';
import toast from 'react-hot-toast';

// Deposit presets are shown in the wallet's display currency for SGD;
// every other wallet keeps the RM presets (deposits are charged in MYR).
const SGD_PRESET_CURRENCIES = ['SGD'];

const CURRENCY_SYMBOLS: Record<string, string> = {
  MYR: 'RM',
  SGD: 'S$',
};

// "Jumlah: S$52.00 (RM 173.33)" for SGD-priced payments, "RM 52.00" otherwise.
function formatPaymentAmount(deposit: {
  amount: number;
  displayCurrency?: string;
  displayAmount?: number;
}): string {
  if (
    deposit.displayCurrency &&
    deposit.displayCurrency !== 'MYR' &&
    deposit.displayAmount != null
  ) {
    const symbol = CURRENCY_SYMBOLS[deposit.displayCurrency] ?? '';
    return `${symbol}${deposit.displayAmount.toFixed(2)} (RM ${deposit.amount.toFixed(2)})`;
  }
  return `RM ${deposit.amount.toFixed(2)}`;
}

export function WalletDeposit({
  isNewWallet,
  pendingDeposit,
  currency,
  activationFee,
  hasReferralDiscount,
}: {
  isNewWallet?: boolean;
  pendingDeposit?: PendingDeposit | null;
  currency?: string;
  activationFee?: ActivationFee;
  /** Referred users get their discount already — promo codes are off for them. */
  hasReferralDiscount?: boolean;
}) {
  const { t } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState<string>('');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  // Promo code (new wallets, MYR only): validated live; the fee line updates
  // to the promo amount. The charge re-validates server-side.
  const [promoCode, setPromoCode] = useState('');
  const [promoState, setPromoState] = useState<{
    status: 'idle' | 'checking' | 'valid' | 'invalid';
    reason?: string;
  }>({ status: 'idle' });
  const [promoFee, setPromoFee] = useState<ActivationFee | null>(null);
  const [depositSuccess, setDepositSuccess] = useState<DepositPaymentResponse | null>(
    pendingDeposit
      ? {
          deposit: {
            id: '',
            referenceNo: pendingDeposit.referenceNo,
            amount: pendingDeposit.amount,
            displayCurrency: pendingDeposit.displayCurrency,
            displayAmount: pendingDeposit.displayAmount,
            status: 'PENDING',
          },
          redipayDetails: {
            payment_id: null,
            reference_no: pendingDeposit.referenceNo,
            payment_url: pendingDeposit.paymentUrl || '',
            status: 'PENDING',
            created_date: '',
          },
        }
      : null,
  );

  const presetCurrency = SGD_PRESET_CURRENCIES.includes(currency ?? '')
    ? (currency as string)
    : 'MYR';
  const presetSymbol = CURRENCY_SYMBOLS[presetCurrency];
  const predefinedAmounts = [50, 100, 200, 500, 1000].map(
    (amount) => `${presetSymbol}${amount}`,
  );

  const handleAmountSelect = (amount: string) => {
    if (amount === 'Lain-lain') {
      setSelectedAmount('custom');
    } else {
      setSelectedAmount(amount);
      setCustomAmount('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // New-wallet welcome card: create activation fee payment (priced per the
    // wallet's display currency; the gateway bills MYR either way).
    if (isNewWallet) {
      try {
        setSubmitting(true);
        const response = await walletService.createActivationFee(
          promoState.status === 'valid' ? promoCode.trim() : undefined,
        );
        setDepositSuccess(response);
        toast.success('Pembayaran yuran diwujudkan!');
      } catch (error: any) {
        console.error('Failed to create activation fee:', error);
        // A promo that went stale between validate and pay shows the server's
        // reason — clear the applied state so the user can retry.
        setPromoFee(null);
        setPromoState({ status: 'idle' });
        toast.error(error.response?.data?.message || 'Gagal mencipta yuran pengaktifan');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const amount = selectedAmount === 'custom'
      ? parseFloat(customAmount)
      : parseFloat(selectedAmount.replace(/[^0-9.]/g, ''));

    if (!amount || amount <= 0) {
      toast.error('Sila masukkan jumlah yang sah');
      return;
    }

    if (amount > 10000) {
      toast.error(`Jumlah maksimum deposit ialah ${presetSymbol} 10,000`);
      return;
    }

    try {
      setSubmitting(true);
      // Amounts are entered in the preset currency; the server charges MYR.
      const response = await walletService.createDeposit(amount, presetCurrency);
      setDepositSuccess(response);
      toast.success('Deposit berjaya dicipta!');
    } catch (error: any) {
      console.error('Failed to create deposit:', error);
      toast.error(error.response?.data?.message || 'Gagal membuat deposit');
    } finally {
      setSubmitting(false);
    }
  };

  if (depositSuccess) {
    return (
      <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-green-200 dark:border-green-800">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-6">
            <Wallet className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Deposit Sedang Diproses</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 text-center">
            Deposit anda telah direkodkan. Sila lengkapkan pembayaran melalui pautan di bawah.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">No. Rujukan</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400 mb-4">
            {depositSuccess.deposit?.referenceNo || 'N/A'}
          </p>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-6">
            Jumlah: {depositSuccess.deposit
              ? formatPaymentAmount(depositSuccess.deposit)
              : 'RM 0.00'}
          </p>
          {depositSuccess.redipayDetails?.payment_url && (
            <>
              <a
                href={depositSuccess.redipayDetails.payment_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors mb-3 text-center"
              >
                Bayar Sekarang
              </a>
              <button
                onClick={() => {
                  setDepositSuccess(null);
                  setSelectedAmount('');
                  setCustomAmount('');
                }}
                className="inline-block w-full py-3 border-2 border-green-600 text-green-600 font-semibold rounded-lg hover:bg-green-600 hover:text-white transition-colors"
              >
                Buat Deposit Baru
              </button>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // Welcome card for brand-new wallets (first visit, no deposits yet)
  if (isNewWallet) {
    // Pricing comes from the API and differs per environment/currency — never
    // render a hardcoded fallback amount.
    const feeFormatted = activationFee?.formatted ?? '—';
    const activeFeeFormatted = promoFee?.formatted ?? feeFormatted;
    // Promo box: MYR wallets only; referred users already have their discount.
    const promoVisible = currency !== 'SGD' && !hasReferralDiscount;

    const handleApplyPromo = async () => {
      const code = promoCode.trim();
      if (!code) {
        toast.error('Sila masukkan kod promo');
        return;
      }
      setPromoState({ status: 'checking' });
      try {
        const result = await walletService.validateActivationPromo(code);
        if (result.valid && result.fee) {
          setPromoFee(result.fee);
          setPromoState({ status: 'valid' });
        } else {
          setPromoFee(null);
          setPromoState({ status: 'invalid', reason: result.reason });
        }
      } catch {
        setPromoState({ status: 'idle' });
        toast.error('Gagal menyemak kod promo');
      }
    };

    const promoErrorMessage = (reason?: string) => {
      switch (reason) {
        case 'MALFORMED':
          return 'Invalid promo code format';
        case 'NOT_FOUND':
          return 'Invalid promo code';
        case 'INACTIVE':
          return 'This promo code is no longer active';
        case 'ALREADY_USED':
          return 'You have already used a promo code';
        case 'REFERRAL_CONFLICT':
          return 'Referral and promo discounts cannot be combined';
        case 'SG_UNSUPPORTED':
          return 'Promo codes are not available for this wallet';
        default:
          return 'Invalid promo code';
      }
    };

    return (
      <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950 dark:to-teal-950 border-emerald-200 dark:border-emerald-800">
        <CardContent className="flex flex-col items-center py-8 px-6">
          <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center mb-6">
            <Wallet className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Welcome!
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 text-center max-w-sm">
            To get started and activate your membership, please deposit {activeFeeFormatted} into your new wallet.
          </p>
          <div className="w-full mb-6">
            <div className="flex items-center justify-between px-4 py-3 border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/50 rounded-lg">
              <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                {promoFee && (
                  <span className="line-through text-emerald-600/50 dark:text-emerald-400/50 mr-2 text-base font-normal">
                    {feeFormatted}
                  </span>
                )}
                {activeFeeFormatted}
              </span>
              <Wallet className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          {promoVisible && (
            <div className="w-full mb-6">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoState({ status: 'idle' });
                    setPromoFee(null);
                  }}
                  placeholder="Promo code (optional)"
                  maxLength={10}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={handleApplyPromo}
                  disabled={promoState.status === 'checking' || !promoCode.trim()}
                  className="px-4 py-2 border-2 border-emerald-600 text-emerald-700 dark:text-emerald-300 font-semibold rounded-lg text-sm hover:bg-emerald-50 dark:hover:bg-emerald-900/50 disabled:opacity-50"
                >
                  {promoState.status === 'checking' ? '…' : 'Apply'}
                </button>
              </div>
              {promoState.status === 'valid' && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  {t('wallet.promoApplied', 'Promo code successfully applied.')}
                </p>
              )}
              {promoState.status === 'invalid' && (
                <p className="text-xs text-red-600 mt-1">{promoErrorMessage(promoState.reason)}</p>
              )}
              {promoState.status !== 'valid' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Have a promo code? Enter it here to reduce the activation fee.
                </p>
              )}
            </div>
          )}
          {hasReferralDiscount && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
              Your referral discount of 50% has been applied to the activation fee.
            </p>
          )}
          <button
            onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
            disabled={submitting}
            className="w-full py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Memproses...' : 'Continue to Payment'}
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wallet className="h-5 w-5" />
          Deposit ke Dompet
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Amount Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Pilih Jumlah Deposit
            </label>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {predefinedAmounts.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => handleAmountSelect(amount)}
                  className={`px-3 py-2 border-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedAmount === amount
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-600 hover:text-blue-600'
                  }`}
                >
                  {amount}
                </button>
              ))}
              <button
                type="button"
                onClick={() => handleAmountSelect('Lain-lain')}
                className={`px-3 py-2 border-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedAmount === 'custom'
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-600 hover:text-blue-600'
                }`}
              >
                Lain-lain
              </button>
            </div>
            {selectedAmount === 'custom' && (
              <div>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder={`Masukkan jumlah lain (${presetSymbol})`}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800"
                  min="1"
                  max="10000"
                />
                {parseFloat(customAmount) > 10000 && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Jumlah maksimum ialah {presetSymbol} 10,000
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>Nota:</strong> Deposit akan ditambah ke baki dompet anda selepas pembayaran berjaya diselesaikan melalui AiAhad.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting || !selectedAmount || (selectedAmount === 'custom' && (!customAmount || parseFloat(customAmount) <= 0))}
            className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Memproses...' : 'Teruskan ke Pembayaran'}
          </button>
        </form>
      </CardContent>
    </Card>
  );
}
